import { useEffect, useState, useCallback } from 'react';
import { Heart, Bookmark, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

/**
 * Universal social strip for designs, products, and stickers.
 *
 * Props:
 *   entityType: 'design' | 'product'
 *   entityId: string
 *   initial?: { liked, saved, likes, saves, rating_avg, rating_count, my_rating }
 *              — pass this if you've already loaded stats (e.g. via /api/social/batch).
 *   size?: 'sm' | 'md'   default 'sm'
 *   layout?: 'compact' | 'full'   default 'compact'
 *   onChange?: (stats) => void
 */
export const SocialStrip = ({
  entityType,
  entityId,
  initial,
  size = 'sm',
  layout = 'compact',
  onChange,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(initial || null);
  const [busy, setBusy] = useState(false);

  const requireAuth = useCallback(() => {
    if (!user) {
      // Non-disruptive: keep the guest on the current page so they can keep
      // browsing. A previous version navigated to /login which felt like
      // "unsigned users cannot view the items" because every tap yanked them
      // out of the library.
      toast('Sign in to like, save or rate', {
        action: { label: 'Sign in', onClick: () => navigate('/login') },
      });
      return false;
    }
    return true;
  }, [user, navigate]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/social/${entityType}/${entityId}`);
      setStats(data);
      onChange && onChange(data);
    } catch {
      /* silent */
    }
  }, [entityType, entityId, onChange]);

  useEffect(() => {
    if (initial) { setStats(initial); return; }
    refresh();
    // eslint-disable-next-line
  }, [entityType, entityId, user?.id]);

  const update = (patch) => {
    // Compute next state synchronously so we can notify the parent, but do
    // NOT call onChange inside a setState UPDATER — that runs during React's
    // render pass and triggers "Cannot update a component while rendering a
    // different component" errors.
    let next;
    setStats((s) => {
      next = { ...(s || {}), ...patch };
      return next;
    });
    if (onChange && next) {
      // Defer to after commit to be safe under React 18's concurrent renderer.
      Promise.resolve().then(() => onChange(next));
    }
  };

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  const toggleLike = async (e) => {
    stop(e);
    if (!requireAuth() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/social/${entityType}/${entityId}/like`);
      update({ liked: data.liked, likes: data.likes });
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const toggleSave = async (e) => {
    stop(e);
    if (!requireAuth() || busy) return;
    setBusy(true);
    try {
      if (stats?.saved) {
        const { data } = await api.delete(`/social/${entityType}/${entityId}/save`);
        update({ saved: false, saves: data.saves });
      } else {
        const { data } = await api.post(`/social/${entityType}/${entityId}/save`);
        update({ saved: data.saved, saves: data.saves });
      }
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const rate = async (n, e) => {
    stop(e);
    if (!requireAuth() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/social/${entityType}/${entityId}/rate`, { stars: n });
      update(data);
      toast.success(`Rated ${n} star${n > 1 ? 's' : ''}`);
    } catch { toast.error('Failed to rate'); }
    finally { setBusy(false); }
  };

  const s = stats || {};
  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const btnSize = size === 'md' ? 'p-2' : 'p-1.5';

  const stars = (
    <div className="flex items-center gap-0.5" data-testid={`social-${entityType}-${entityId}-rating`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= (s.my_rating || Math.round(s.rating_avg || 0));
        return (
          <button
            type="button"
            key={n}
            onClick={(e) => rate(n, e)}
            data-testid={`social-${entityType}-${entityId}-star-${n}`}
            className={`p-0.5 leading-none hover:scale-110 transition-transform ${busy ? 'opacity-70 cursor-wait' : ''}`}
            aria-label={`Rate ${n} of 5`}
          >
            <Star
              className={`${iconSize} ${active ? 'text-amber-400 fill-amber-400' : ''}`}
              style={{ color: active ? undefined : 'var(--jw-text-subtle)' }}
            />
          </button>
        );
      })}
    </div>
  );

  if (layout === 'full') {
    return (
      <div className="flex items-center gap-3 text-[11px]" style={{ color: 'var(--jw-text-muted)' }}>
        <button
          onClick={toggleLike}
          data-testid={`social-${entityType}-${entityId}-like-button`}
          className={`flex items-center gap-1 rounded-full px-2.5 h-8 border transition-colors ${s.liked ? 'bg-pink-500/85 text-white border-white/20' : 'border-white/10 hover:bg-white/[0.06]'}`}
        >
          <Heart className={`${iconSize} ${s.liked ? 'fill-white' : ''}`} />
          <span>{s.likes || 0}</span>
        </button>
        <button
          onClick={toggleSave}
          data-testid={`social-${entityType}-${entityId}-save-button`}
          className={`flex items-center gap-1 rounded-full px-2.5 h-8 border transition-colors ${s.saved ? 'bg-orange-500/85 text-white border-white/20' : 'border-white/10 hover:bg-white/[0.06]'}`}
        >
          <Bookmark className={`${iconSize} ${s.saved ? 'fill-white' : ''}`} />
          <span>{s.saves || 0}</span>
        </button>
        <div className="flex flex-col leading-tight">
          {stars}
          <span className="text-[10px]">
            {s.rating_count ? `${s.rating_avg}/5 · ${s.rating_count}` : 'Be the first'}
          </span>
        </div>
      </div>
    );
  }

  // compact — for grid cards
  return (
    <div className="flex items-center justify-between gap-2">
      <div>
        {stars}
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--jw-text-subtle)' }}>
          {s.rating_count ? `${s.rating_avg}/5 · ${s.rating_count}` : 'Be the first'}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={toggleLike}
          data-testid={`social-${entityType}-${entityId}-like-button`}
          className={`${btnSize} rounded-full border transition-colors flex items-center gap-1 text-[11px] ${s.liked ? 'bg-pink-500/85 text-white border-white/20' : 'border-white/10 hover:bg-white/[0.06]'}`}
          style={{ color: s.liked ? undefined : 'var(--jw-text-muted)' }}
          aria-label={s.liked ? 'Unlike' : 'Like'}
        >
          <Heart className={`${iconSize} ${s.liked ? 'fill-white' : ''}`} />
          <span>{s.likes || 0}</span>
        </button>
        <button
          onClick={toggleSave}
          data-testid={`social-${entityType}-${entityId}-save-button`}
          className={`${btnSize} rounded-full border transition-colors flex items-center gap-1 text-[11px] ${s.saved ? 'bg-orange-500/85 text-white border-white/20' : 'border-white/10 hover:bg-white/[0.06]'}`}
          style={{ color: s.saved ? undefined : 'var(--jw-text-muted)' }}
          aria-label={s.saved ? 'Unsave' : 'Save'}
        >
          <Bookmark className={`${iconSize} ${s.saved ? 'fill-white' : ''}`} />
          <span>{s.saves || 0}</span>
        </button>
      </div>
    </div>
  );
};

export default SocialStrip;
